import { useData } from "./useData";
import {
  reservationRepository,
  ReservationResponse,
} from "@/repositories/ReservationRepository";
import { ReservationStatus } from "@/types";

// Payment status type (not in database enums, so we define it)
type PaymentStatus = "pending" | "paid" | "refunded";

export interface ReservationFilters {
  status?: ReservationStatus;
  payment_status?: PaymentStatus;
  property_id?: string;
  date_range?: {
    start: Date;
    end: Date;
  };
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  search?: string;
}

export const statusOptions = {
  waiting_checkin: { label: "Aguardando check-in", color: "yellow" },
  guest_in_house: { label: "Hóspede na casa", color: "green" },
  checkout_done: { label: "Check-out feito", color: "blue" },
} as const;

export const paymentStatusOptions = {
  pending: { label: "Pendente", color: "yellow" },
  paid: { label: "Pago", color: "green" },
  refunded: { label: "Reembolsado", color: "purple" },
} as const;

export function useReservations(filters: ReservationFilters = {}) {
  const { data, isLoading, error, refetch } = useData<ReservationResponse>({
    repository: reservationRepository,
    fetcher: (repo) => {
      const queryFilters: Record<string, any> = { ...filters };

      if (filters.date_range) {
        queryFilters.startDate = filters.date_range.start
          .toISOString()
          .split("T")[0];
        queryFilters.endDate = filters.date_range.end
          .toISOString()
          .split("T")[0];
        delete queryFilters.date_range;
      }

      return repo.getAll(queryFilters);
    },
    queryKey: ["reservations", JSON.stringify(filters)],
    options: {
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  });

  const paginatedData = data as {
    items: ReservationResponse[];
    count: number;
  } | null;

  return {
    reservations: paginatedData?.items ?? [],
    count: paginatedData?.count ?? 0,
    isLoading,
    error,
    refetch,
  };
}
